import { useQuery } from "@tanstack/react-query";
import { useTenant } from "./useTenant";
import { supabase } from "@/integrations/supabase/client";

interface FamilyExposure {
  family_id: string;
  family_name: string;
  commitment: number;
  nav: number;
  paid_in: number;
  position_id: string;
}

interface InvestmentDetail {
  id: string;
  name: string;
  type: string;
  issuer: string | null;
  currency: string | null;
  metadata: {
    strategy?: string;
    vintage_year?: number;
    [key: string]: unknown;
  } | null;
  // Cross-family data
  familyCount: number;
  totalCommitment: number;
  totalNav: number;
  totalPaidIn: number;
  familyExposures: FamilyExposure[];
}

interface PositionMetrics {
  commitment_base: number | null;
  nav_base: number | null;
  paid_in_base: number | null;
  distributed_base: number | null;
  tvpi: number | null;
  dpi: number | null;
  irr_itd: number | null;
  period_end: string;
}

/**
 * Hook to fetch investment/instrument details with cross-family exposure data.
 * Used by InvestmentDetailDrawer to show which families hold an investment.
 */
export function useInvestmentDetail(instrumentId: string | null) {
  const { orgId } = useTenant();

  return useQuery<InvestmentDetail | null>({
    queryKey: ['investment-detail', instrumentId, orgId],
    queryFn: async () => {
      if (!instrumentId || !orgId) return null;

      // 1. Get instrument details
      const { data: instrument, error: instrumentError } = await supabase
        .from('instruments')
        .select('id, name, type, issuer, currency, metadata')
        .eq('id', instrumentId)
        .single();

      if (instrumentError) throw instrumentError;
      if (!instrument) return null;

      // 2. Get cross-family exposure for this instrument
      const { data: crossExposure } = await supabase
        .from('cross_family_exposure')
        .select('*')
        .eq('org_id', orgId)
        .eq('instrument_id', instrumentId)
        .order('period_end', { ascending: false })
        .limit(1)
        .maybeSingle();

      return {
        id: instrument.id,
        name: instrument.name,
        type: instrument.type,
        issuer: instrument.issuer,
        currency: instrument.currency,
        metadata: instrument.metadata as InvestmentDetail['metadata'],
        familyCount: crossExposure?.family_count || 0,
        totalCommitment: crossExposure?.total_commitment_base || 0,
        totalNav: crossExposure?.total_nav_base || 0,
        totalPaidIn: crossExposure?.total_paid_in_base || 0,
        familyExposures: (crossExposure?.family_exposures || []) as FamilyExposure[],
      };
    },
    enabled: !!instrumentId && !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a specific family's position metrics for an instrument.
 * Used when viewing investment detail from a family context.
 */
export function usePositionMetrics(positionId: string | null) {
  return useQuery<PositionMetrics | null>({
    queryKey: ['position-metrics', positionId],
    queryFn: async () => {
      if (!positionId) return null;

      const { data, error } = await supabase
        .from('position_metrics')
        .select('commitment_base, nav_base, paid_in_base, distributed_base, tvpi, dpi, irr_itd, period_end')
        .eq('position_id', positionId)
        .order('period_end', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!positionId,
    staleTime: 5 * 60 * 1000,
  });
}
